import {
  Text,
  TextButton,
  Button,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { useAssets } from "expo-asset";
import { useEffect, useState } from "react";
import { FlatList } from "react-native";
import { Image, Spacings, View } from "react-native-ui-lib";
import { useLocalSettingsProvider } from "../../../../../../utilities/local-settings";
import { moneyToFormattedString } from "../../../../../../utilities/money";
import { usePrint } from "../../../../../../utilities/print";
import { toHtml, toHtmlPage } from "../../../../../../utilities/to-html-page";
import { useTyroIClient } from "../../../../../../utilities/tyro/hooks.web";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import { MakeTyroPaymentProps } from "./types";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";
import Constants from "expo-constants";
import { version } from "../../../../../../../package.json";
import { usePaymentActionsContextProvider } from "../../../PaymentRootScreen/hooks";

type Question = {
  options: string[];
  text: string;
  onAnswer: (answer: string) => void;
};

export const MakeTyroPayment = ({
  sale,
  paymentMethod,
  navigation,
  mode,
}: MakeTyroPaymentProps) => {
  const maybeParentScreenContext = usePaymentActionsContextProvider();
  const { print } = usePrint();
  const { colors } = useThemeProvider();
  const [assets] = useAssets([
    require("../../../../../../../assets/images/logo-tyro.png"),
  ]);

  const {
    settings: { integratedTyroReceiptPrinting, printMerchantReceipt },
  } = useLocalSettingsProvider();

  const [question, setQuestion] = useState<Question | undefined>(undefined);
  const [message, setMessage] = useState<string>("");
  const [tyroState, setTyroState] = useState<string | undefined>(undefined);

  const { iClient, initialized } = useTyroIClient({
    mode: mode ?? "PROD",
    apiKey: Constants.manifest?.extra?.tyroApiKey,
    posProductName: "Roam V2",
    posProductVersion: version,
    posProductVendor: "Tower Systems",
  });

  const {
    tenderedAmountInput,
    validTenderedAmount,
    addPayment,
    tenderedAmountAsInteger,
    isRefundAmount,
  } = usePaymentControllerProvider();

  useEffect(() => {
    navigation.setOptions({
      cardStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
      },
      headerStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
        borderBottomColor: "rgba(255,255,255,0.2)",
      },
      headerTitle: () => null,
      headerLeft: () => (
        <View style={{ marginLeft: Spacings.s4 }}>
          <TextButton
            textStyle={{ color: colors.white }}
            onPress={() => {
              if (tyroState) {
                navigation.goBack();
                return;
              }
              try {
                setMessage("Cancel current transaction?");
                iClient?.cancelCurrentTransaction();
              } catch (e: any) {
                if (
                  e.message.includes(
                    "There is no in-progress transaction to cancel"
                  )
                ) {
                  navigation.goBack();
                }
              }
            }}
          >
            {tyroState ? "Close" : "Cancel"}
          </TextButton>
        </View>
      ),
    });
  }, [iClient, colors, tyroState, isRefundAmount]);

  /*
  React.useEffect(() => {
    if (tyroState === "CANCELLED") {
      navigation.goBack();
    }
  }, [tyroState]);
  */

  useEffect(() => {
    if (initialized && tenderedAmountAsInteger !== undefined) {
      if (isRefundAmount) {
        iClient.initiateRefund(
          {
            amount: Math.abs(tenderedAmountAsInteger).toString(),
            integratedReceipt: integratedTyroReceiptPrinting,
            enableSurcharge: true,
          },
          {
            statusMessageCallback: handleMessageCallback,
            questionCallback: handleQuestionCallback,
            receiptCallback: handleReceiptCallback,
            transactionCompleteCallback: handleTransactionCompleteCallback,
          }
        );
      } else {
        iClient.initiatePurchase(
          {
            amount: tenderedAmountAsInteger.toString(),
            integratedReceipt: integratedTyroReceiptPrinting,
            enableSurcharge: true,
          },
          {
            statusMessageCallback: handleMessageCallback,
            questionCallback: handleQuestionCallback,
            receiptCallback: handleReceiptCallback,
            transactionCompleteCallback: handleTransactionCompleteCallback,
          }
        );
      }
    }
  }, [initialized]);

  const handleOnApprovedPayment = async (options?: AddPaymentOptions) => {
    try {
      const tenderedAmount =
        options?.tenderedAmount || options?.tenderedAmount === 0
          ? options.tenderedAmount
          : parseFloat(tenderedAmountInput);
      await maybeParentScreenContext?.onAddPayment?.({
        ...options,
        paymentAmount: options?.paymentAmount?.toString() || "0",
        paymentMethod: paymentMethod,
        tenderedAmount: tenderedAmount.toString(),
      });
    } catch (err) {}
  };

  if (!initialized) {
    return null;
  }

  if (!validTenderedAmount) {
    return null;
  }

  const handleReceiptCallback = (response: any) => {
    if (
      (response.merchantReceipt && response.signatureRequired == true) ||
      (response.merchantReceipt && printMerchantReceipt)
    ) {
      print(toHtmlPage(response.merchantReceipt));
    }
  };

  const handleQuestionCallback = (question: any, answerCallback: any) => {
    if (question.text) {
      setMessage(question.text);
    }
    setQuestion({
      text: "",
      options: question.options,
      onAnswer: (answer: string) => {
        setQuestion(undefined);
        setMessage("");
        answerCallback(answer);
      },
    });
  };

  const handleMessageCallback = (message: string) => {
    setMessage(message);
  };

  const handleTransactionCompleteCallback = (response: any) => {
    const { result, baseAmount, customerReceipt, ...rest } = response;

    if (result) {
      setTyroState(result);
    }

    if (["APPROVED"].includes(result)) {
      let surcharge = parseFloat(response.surchargeAmount ?? 0);
      surcharge = isNaN(surcharge) ? 0 : surcharge;
      let total = formatToMoney(surcharge + parseFloat(tenderedAmountInput));
      return handleOnApprovedPayment({
        tenderedAmount: total,
        surcharge,
        transactionId: response.transactionId,
        name: `${paymentMethod.name} (${response.cardType})`,
        metadata: {
          customerReceipt: customerReceipt
            ? toHtml(customerReceipt)
            : undefined,
          ...rest,
        },
      });
    }

    // if there is any other state and their is a customer receipt, it needs to be saved
    // and printed when the sale finally completes
    if (customerReceipt) {
      print(toHtmlPage(customerReceipt));
      return;
    }

    if (["NOT STARTED", "UNKNOWN"].includes(result)) {
      navigation.navigate("SelectPaymentMethodScreen");
    }
  };

  return (
    <View
      flex
      style={{
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View style={{ justifyContent: "center", alignItems: "center" }}>
        <View style={{ marginBottom: Spacings.s5 * 2 }}>
          {assets ? (
            <Image style={{ width: 200, height: 90 }} source={assets[0]} />
          ) : null}
        </View>
        <View
          style={{
            alignItems: "center",
          }}
        >
          <Text
            style={{
              textAlign: "center",
              fontSize: 20,
              opacity: 0.8,
              marginBottom: Spacings.s2,
              color: colors.white,
            }}
          >
            {isRefundAmount ? "Refund" : "Purchase"}
          </Text>
          <Text
            style={{
              fontSize: 30,
              textAlign: "center",
              marginBottom: Spacings.s4,
              color: colors.white,
            }}
          >
            {moneyToFormattedString(Math.abs(Number(tenderedAmountInput)))}
          </Text>
          <Text
            style={{
              textAlign: "center",
              fontSize: 18,
              marginBottom: Spacings.s6,
              color: colors.white,
            }}
          >
            {message}
          </Text>
        </View>

        {question ? (
          <View row style={{ alignItems: "center" }}>
            <Text style={{ fontSize: 18, marginBottom: Spacings.s4 }}>
              {question.text}
            </Text>
            <View style={{ minHeight: 30 }}>
              <FlatList
                style={{ marginBottom: Spacings.s4 }}
                horizontal
                scrollEnabled={true}
                ItemSeparatorComponent={() => (
                  <View
                    style={{
                      height: "100%",
                      width: Spacings.s4 * 2,
                    }}
                  ></View>
                )}
                data={question.options.map((option) => ({
                  key: option,
                  label: option,
                  onPress: () => {
                    question.onAnswer(option);
                  },
                }))}
                renderItem={({ item }) => {
                  return <Button {...item} />;
                }}
              ></FlatList>
            </View>
          </View>
        ) : (
          <View style={{ minHeight: 60, width: 20 }} />
        )}
      </View>
    </View>
  );
};

function formatToMoney(num: number) {
  return Math.round(num * 100 + Number.EPSILON) / 100;
}
